import React, { useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export const CambiarContrasena = () => {
  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast();

  const [actualPassword, setActualPassword] = useState("");
  const [nuevaPassword, setNuevaPassword] = useState("");
  const [confirmarPassword, setConfirmarPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  // =========================
  // Validaciones
  // =========================
  const pwdMismatch = useMemo(() => {
    if (!nuevaPassword && !confirmarPassword) return false;
    return nuevaPassword !== confirmarPassword;
  }, [nuevaPassword, confirmarPassword]);

  // =========================
  // Submit
  // =========================
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!actualPassword || !nuevaPassword || !confirmarPassword) {
      toast.warning("Todos los campos son obligatorios.");
      return;
    }

    if (nuevaPassword.length < 8) {
      toast.warning("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (pwdMismatch) {
      toast.warning("Las nuevas contraseñas no coinciden.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}usuarios/cambiar-contrasena`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          actualPassword,
          nuevaPassword,
          confirmarPassword,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "No se pudo cambiar la contraseña.");
        return;
      }

      toast.success("Contraseña actualizada correctamente.");

      // Reset
      setActualPassword("");
      setNuevaPassword("");
      setConfirmarPassword("");
    } catch (err) {
      console.error("Error al cambiar la contraseña:", err);
      toast.error("Error de conexión con el servidor.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Cambiar contraseña
        </Typography>

        {/* ✅ Semántica oficial de formulario single-column */}
        <Box className="contentContainer contentContainer--formSingle">
          <Paper sx={{ p: 2 }}>
            <Box component="form" onSubmit={handleChangePassword}>
              <Stack spacing={2}>
                {/* Contraseña actual */}
                <TextField
                  label="Contraseña actual"
                  type={showPassword ? "text" : "password"}
                  value={actualPassword}
                  onChange={(e) => setActualPassword(e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                          aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Nueva contraseña */}
                <TextField
                  label="Nueva contraseña"
                  type={showPassword ? "text" : "password"}
                  value={nuevaPassword}
                  onChange={(e) => setNuevaPassword(e.target.value)}
                  required
                  helperText="Mínimo 8 caracteres"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Confirmar nueva contraseña */}
                <TextField
                  label="Confirmar nueva contraseña"
                  type={showPassword ? "text" : "password"}
                  value={confirmarPassword}
                  onChange={(e) => setConfirmarPassword(e.target.value)}
                  required
                  error={pwdMismatch}
                  helperText={pwdMismatch ? "Las contraseñas no coinciden." : " "}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Acciones */}
                <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setActualPassword("");
                      setNuevaPassword("");
                      setConfirmarPassword("");
                      toast.info("Formulario limpiado.");
                    }}
                    disabled={saving}
                  >
                    Limpiar
                  </Button>

                  <Button type="submit" disabled={saving || pwdMismatch} aria-busy={saving}>
                    {saving ? "Actualizando..." : "Actualizar contraseña"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
